import { useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { API_CONFIG } from '../config/api';

interface UseSignalROptions {
  onAdminNotification?: (notification: any) => void;
  onCustomerNotification?: (notification: any) => void;
}

// Singleton connection to prevent multiple connections in StrictMode
let globalConnection: signalR.HubConnection | null = null;
let connectionPromise: Promise<void> | null = null;

export const useSignalR = (options: UseSignalROptions) => {
  const mountedRef = useRef(true);
  
  // Use refs for callbacks to avoid reconnection on callback changes
  const onAdminNotificationRef = useRef(options.onAdminNotification);
  const onCustomerNotificationRef = useRef(options.onCustomerNotification);
  
  // Update refs when callbacks change
  useEffect(() => {
    onAdminNotificationRef.current = options.onAdminNotification;
  }, [options.onAdminNotification]);
  
  useEffect(() => {
    onCustomerNotificationRef.current = options.onCustomerNotification;
  }, [options.onCustomerNotification]);

  const connect = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // If already connected, just return
    if (globalConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    // If connection is in progress, wait for it
    if (connectionPromise) {
      await connectionPromise;
      return;
    }

    // Create new connection
    connectionPromise = (async () => {
      try {
        // Stop any existing connection first
        if (globalConnection) {
          await globalConnection.stop();
          globalConnection = null;
        }

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(`${API_CONFIG.BASE_URL}/hubs/notifications`, {
            accessTokenFactory: () => localStorage.getItem('accessToken') || '',
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        // Handle admin notifications - use ref to always get latest callback
        connection.on('ReceiveAdminNotification', (notification) => {
          onAdminNotificationRef.current?.(notification);
        });

        // Handle customer notifications - use ref to always get latest callback
        connection.on('ReceiveNotification', (notification) => {
          onCustomerNotificationRef.current?.(notification);
        });

        connection.onclose((error) => {
          globalConnection = null;
          connectionPromise = null;
        });

        connection.onreconnecting((error) => {
        });

        connection.onreconnected((connectionId) => {
        });

        await connection.start();
        globalConnection = connection;
      } catch (error) {
        console.error('SignalR connection error:', error);
        globalConnection = null;
      } finally {
        connectionPromise = null;
      }
    })();

    await connectionPromise;
  }, []);

  const disconnect = useCallback(async () => {
    if (globalConnection) {
      await globalConnection.stop();
      globalConnection = null;
      connectionPromise = null;
    }
  }, []);

  // Connect once on mount
  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      // Don't disconnect on unmount in StrictMode - connection is shared
      // Only disconnect if this is a real unmount (e.g., logout)
    };
  }, [connect]);

  return {
    connection: globalConnection,
    connect,
    disconnect,
    isConnected: globalConnection?.state === signalR.HubConnectionState.Connected,
  };
};
